import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import type { ApiEmbedSearchWidgetId } from '#/api/types/ApiTypedId';
import { useLatest } from '#/hooks/useLatest';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchContextHeaderLocationState } from '#/modules/search/containers/SearchContextHeader/SearchContextHeaderLocationState';
import {
  useSearchActions,
  useSearchGetters,
  useSearchLocation,
  useSearchQuery,
} from '#/modules/search/contexts/SearchContext';
import { getParsedSearchQuery } from '#/modules/search/helpers/getParsedSearchQuery';
import { useSearchPageActions } from '#/modules/search/hooks/useSearchPageActions';
import type { SearchPageLocationState } from '#/modules/search/pages/Search/types/SearchPageLocationState';
import { searchGetRoutePathSwitch } from '#/modules/search/routing/helpers/searchGetRoutePathSwitch';
import type { SearchFromPageType } from '#/modules/search/types/SearchFromPageType';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';
import type { SearchType } from '#/modules/search/types/SearchType';
import { EMPTY_SEARCH_LOCATION } from '#/utils/constants/general/emptySearchLocation';
import { truncateToUTF8Bytes } from '#/utils/string/truncateToUTF8Bytes';
import { deferWithBriefLoadingAnimation } from '#/utils/ui/routeLoadingAnimation';

type Args = {
  defaultSearchType: SearchType;
  isInSearchPage: boolean;
  isInSearchLandingPage: boolean;
  embedSearchWidgetId: ApiEmbedSearchWidgetId | null;
  searchFromPageType?: SearchFromPageType;
};
export function usePageHeaderContainerSearch({
  defaultSearchType,
  isInSearchPage,
  isInSearchLandingPage,
  embedSearchWidgetId,
  searchFromPageType,
}: Args) {
  const { locale } = useI18n();
  const location = useLocation<SearchContextHeaderLocationState>();
  const navigate = useNavigate();

  const { setSearchUrlQueryOrLocationChanged } = useSearchPageActions({
    isInSearchLandingPage,
  });

  const [searchParams] = useSearchParams();
  const searchParamsRef = useLatest(searchParams);

  const reduxQuery = useSearchQuery();
  const initialQuery = useMemo(() => {
    if (
      (reduxQuery && location.state?.showSearchContext) ||
      isInSearchLandingPage
    ) {
      return reduxQuery;
    }

    return truncateToUTF8Bytes(searchParams.get('q') ?? '', 512);
  }, [
    isInSearchLandingPage,
    location.state?.showSearchContext,
    reduxQuery,
    searchParams,
  ]);

  const searchLocation = useSearchLocation();

  const { updateSearchLocation } = useSearchActions();
  const { getSearchLocation } = useSearchGetters();

  const changeSearchLocation = useCallback(
    (newSearchLocation: SearchLocation | undefined): boolean => {
      if (newSearchLocation) {
        // The LocationInput triggers the change event twice except for the `Remote` option
        if (
          newSearchLocation.text &&
          getSearchLocation()?.text === newSearchLocation.text
        )
          return false;

        deferWithBriefLoadingAnimation(() => {
          updateSearchLocation(newSearchLocation, true);
        });
      } else {
        deferWithBriefLoadingAnimation(() => {
          updateSearchLocation(EMPTY_SEARCH_LOCATION, true);
        });
      }

      return true;
    },
    [getSearchLocation, updateSearchLocation],
  );

  const search = useCallback(
    (data: {
      listingType: SearchType;
      query: string;
      radius: string | null;
      triggeredByRemoteLocationChange: boolean;
    }) => {
      if (isInSearchPage && data.listingType === defaultSearchType) {
        setSearchUrlQueryOrLocationChanged(
          {
            query: data.query,
            triggeredByRemoteLocationChange:
              data.triggeredByRemoteLocationChange,
          },
          { searchFrom: 'Header Search Bar', searchFromPageType },
        );
      } else {
        const { parsedQuery, locationType, remoteKeywords } =
          getParsedSearchQuery({ query: data.query, locale });

        navigate(
          searchGetRoutePathSwitch({
            locale,
            filtersByName: {
              type: data.listingType,
              locationType: data.triggeredByRemoteLocationChange
                ? ['REMOTE']
                : locationType,
            },
            q: parsedQuery,
            // We wipe everything but the radius when a location is set
            radius: getSearchLocation()?.text
              ? (data.radius ?? searchParamsRef.current.get('radius'))
              : undefined,
            embedSearchWidgetId,
          }),
          {
            state: {
              searchFrom: 'Header Search Bar',
              searchFromPageType,
              remoteKeywords,
            } satisfies SearchPageLocationState,
          },
        );
      }
    },
    [
      locale,
      defaultSearchType,
      getSearchLocation,
      isInSearchPage,
      navigate,
      searchFromPageType,
      searchParamsRef,
      setSearchUrlQueryOrLocationChanged,
      embedSearchWidgetId,
    ],
  );

  return {
    initialSearchType: defaultSearchType,
    initialQuery,
    searchLocation,
    changeSearchLocation,
    search,
    searchOnClear: isInSearchPage,
  };
}
